import {
	Dropdown,
	companyNatureOptions,
	locationPreferenceOptions,
	natureOfBusinessOptions,
	ownershipOptions,
} from 'shared-utils';
import React, { useState } from 'react';
import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import Select, { SingleValue } from 'react-select';
import { CompanyCreateInfo, CompanyNature, CompanyOwnershipType, CompanyType, NatureOfBusinessType } from 'types';
import { useMutation, useQueryClient } from 'react-query';
import { createCompany } from 'src/apis/company';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';

function AMCreateCompany() {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [companyDetail, setCompanyDetail] = useState<CompanyCreateInfo>({
		companyType: CompanyType.LARGE_ENTERPRISE,
		name: '',
		url: '',
	});

	const createCompanyFn = useMutation(['createCompanyMutation'], createCompany, {
		onSuccess(data) {
			queryClient.refetchQueries(['getMyCompaniesQuery']);
			navigate(`/my-positions/${data?.id}`);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleChange = (change: Partial<CompanyCreateInfo>) => {
		setCompanyDetail({
			...companyDetail,
			...change,
		});
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		createCompanyFn.mutate(companyDetail);
	};

	const labelStyle = `dark:text-white text-[#111928]`;
	return (
		<div className="md:py-6">
			<div className="max-w-[580px] mx-auto dark:bg-[#111E2C] bg-[#fff] p-4 md:p-6 md:rounded-lg border dark:border-0">
				<h1 className="text-xl md:text-2xl font-bold dark:text-white text-[#28293D] mb-2">Create New Company</h1>

				<form className="flex font-inter flex-col gap-4 mt-[20px]" onSubmit={handleSubmit}>
					{/* Company Name */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="name" value="Company Name" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput type="text" onChange={e => handleChange({ name: e.target.value })} placeholder="LevelUp" />
					</div>

					{/* Company URL */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Company URL" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="text"
							onChange={e => handleChange({ url: e.target.value })}
							placeholder="https://www.levelup.masaischool.com"
						/>
					</div>

					{/* Location */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headQuarter" value="Location" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={locationPreferenceOptions.map(l => ({ label: l, value: l }))}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) => handleChange({ headQuarter: val?.value })}
							placeholder="Bengaluru, Karnataka"
						/>
					</div>

					{/* Industry Type */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="nature" value="Industry Type" className={`${labelStyle}`} />
						</div>
						<Select
							options={companyNatureOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ nature: val?.value, industry: val?.value === CompanyNature.PRODUCT ? '1' : '2' })
							}
							placeholder="Product"
						/>
					</div>

					{/* Founded In */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Founded In" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="number"
							step={1}
							min={1800}
							max={moment().year()}
							onChange={e => handleChange({ foundedIn: Number(e.target.value) })}
							placeholder="2023"
						/>
					</div>

					{/* Head Count */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headCount" value="Head Count" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="number"
							step={1}
							min={0}
							onChange={e => handleChange({ headCount: Number(e.target.value) })}
							placeholder="100"
						/>
					</div>

					{/* Ownership */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="ownership" value="Ownership" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={ownershipOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) => handleChange({ ownership: val?.value as CompanyOwnershipType })}
							placeholder="Product"
						/>
					</div>

					{/* Nature of Business */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="natureOfBusiness" value="Nature of Business" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={natureOfBusinessOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ natureOfBusiness: val?.value as NatureOfBusinessType })
							}
							placeholder="Product"
						/>
					</div>

					{/* Pre-Placement Call Recording */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="prePlacementCallRecording" value="Pre-Placement Call Recording Link" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="url"
							onChange={e => handleChange({ prePlacementCallRecording: e.target.value })}
							placeholder="https://..."
							value={companyDetail.prePlacementCallRecording || ''}
						/>
					</div>

					<div className="bg-white dark:bg-[#111E2C] sticky bottom-0 mb-[-16px] md:mb-[-24px] mx-[-16px] md:mx-[-24px] shadow-up px-[16px] md:px-[24px] rounded-b-md">
						<Button
							type="submit"
							color="purplePlaced"
							className="my-3 w-full"
							disabled={
								!companyDetail.name ||
								!companyDetail.url ||
								!companyDetail.headQuarter ||
								!companyDetail.foundedIn ||
								!companyDetail.ownership ||
								!companyDetail.natureOfBusiness ||
								createCompanyFn.isLoading
							}
						>
							{createCompanyFn.isLoading ? <Spinner /> : 'Submit'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default AMCreateCompany;
